import axios from 'axios';
import { buildLlmCrawlerPath } from 'src/utils/app';
// NOTE: Do NOT import availableRetailers for Digital Shelf matching. Use API-provided patterns instead.

export interface RetailerRecord {
  retailerId?: string | number;
  id?: string | number;
  retailerName?: string;
  name?: string;
}

export interface RetailerPattern {
  id: string;
  candidates: string[]; // URLs or host strings for this retailer
}

export const fetchRetailerNameMap = async (): Promise<Record<string, string>> => {
  const path = buildLlmCrawlerPath('retailers');
  let data: any = null;
  try {
    ({ data } = await axios.get(path));
  } catch (_e) {
    return {};
  }
  const list: RetailerRecord[] = Array.isArray(data?.retailers) ? data.retailers : [];
  return list.reduce((acc, r) => {
    const id = String(r?.retailerId ?? '');
    const name = r?.retailerName || '';
    if (id) acc[id] = name;
    return acc;
  }, {} as Record<string, string>);
};

/**
 * Derive a retailer id from a product URL using known retailer URL patterns
 */
export const getRetailerIdFromUrl = (url: string, retailerPatterns: RetailerPattern[] = []): string | null => {
  try {
    const { hostname } = new URL(url);
    if (!hostname) return null;
    const host = hostname.replace(/^www\./i, '');
    for (const r of retailerPatterns) {
      for (const candidate of r.candidates) {
        if (!candidate || typeof candidate !== 'string') continue;
        try {
          const candHost = new URL(candidate).hostname.replace(/^www\./i, '');
          if (host.endsWith(candHost)) {
            return String(r.id ?? '');
          }
        } catch (_e) {
          const c = candidate.replace(/^www\./i, '');
          if (c && (c.includes(host) || host.includes(c))) {
            return String(r.id ?? '');
          }
        }
      }
    }
    return null;
  } catch (_e) {
    return null;
  }
};

/**
 * Group a list of URLs by retailer id. Unmatched URLs are ignored.
 */
export const groupUrlsByRetailerId = (list: string[], retailerPatterns: RetailerPattern[] = []): Record<string, string[]> => {
  return list.reduce((acc, url) => {
    const rid = getRetailerIdFromUrl(url, retailerPatterns);
    if (!rid) return acc;
    acc[rid] = acc[rid] ? acc[rid].concat(url) : [url];
    return acc;
  }, {} as Record<string, string[]>);
};
